/**
 * Debt Manager Page - Auxilio Finance
 * Track debts, plan payoff strategies, and monitor progress
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ChartBarIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CreditCardIcon,
  BanknotesIcon,
  CalculatorIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  HomeIcon,
  TruckIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  Area,
  ComposedChart,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useThemeStore } from '../store/themeStore';

interface Debt {
  id: string;
  name: string;
  type: string;
  balance: number;
  original_balance: number;
  interest_rate: number;
  minimum_payment: number;
  due_date?: number;
  institution?: string;
  account_number?: string;
  notes?: string;
  start_date?: string;
  priority?: number;
  created_at?: string;
  updated_at?: string;
}

interface DebtCategory {
  id: string;
  name: string;
  icon: string;
  typical_rate_range: string;
  description: string;
}

interface Payment {
  id: string;
  debt_id: string;
  amount: number;
  payment_date: string;
  principal_paid?: number;
  interest_paid?: number;
  notes?: string;
}

interface DebtSummary {
  total_debt: number;
  total_minimum_payments: number;
  average_interest_rate: number;
  highest_interest_debt: string | null;
  lowest_balance_debt: string | null;
  debt_count: number;
  debts_by_type: Record<string, number>;
  total_monthly_interest: number;
}

interface PayoffPlan {
  strategy: string;
  total_months: number;
  total_interest: number;
  payoff_order: Array<{
    debt_id: string;
    debt_name: string;
    balance: number;
    interest_rate: number;
    payoff_month: number;
    total_interest_paid: number;
  }>;
  monthly_payments: Array<{
    month: number;
    debts: Array<{
      debt_id: string;
      payment: number;
      remaining_balance: number;
    }>;
    total_payment: number;
  }>;
}

interface StrategyComparison {
  avalanche: {
    total_months: number;
    total_interest: number;
    monthly_payment: number;
  };
  snowball: {
    total_months: number;
    total_interest: number;
    monthly_payment: number;
  };
  interest_savings: number;
  time_difference: number;
  recommendation: string;
}

interface ConsolidationAnalysis {
  current_situation: {
    total_debt: number;
    total_monthly_payment: number;
    weighted_avg_rate: number;
    total_interest: number;
    months_to_payoff: number;
  };
  consolidation_option: {
    new_rate: number;
    new_monthly_payment: number;
    total_interest: number;
    months_to_payoff: number;
  };
  savings: {
    monthly_savings: number;
    interest_savings: number;
    time_savings: number;
  };
  recommendation: string;
  considerations: string[];
}

interface AIAnalysis {
  summary: {
    total_debt: number;
    average_rate: number;
    monthly_payments: number;
    debt_to_income_estimate: string;
  };
  debt_health_score: number;
  priority_debts: string[];
  quick_wins: string[];
  strategy_recommendation: string;
  action_plan: string[];
  warnings: string[];
  encouragement: string;
}

const DEBT_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6',
  '#a855f7', '#ec4899',
];

const STRATEGY_COLORS = {
  avalanche: '#3b82f6',
  snowball: '#22c55e',
};

export const DebtManager: React.FC = () => {
  const { t } = useTranslation();
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'debts' | 'payoff' | 'analysis'>('overview');
  const [debts, setDebts] = useState<Debt[]>([]);
  const [categories, setCategories] = useState<DebtCategory[]>([]);
  const [summary, setSummary] = useState<DebtSummary | null>(null);
  const [payoffPlan, setPayoffPlan] = useState<PayoffPlan | null>(null);
  const [strategyComparison, setStrategyComparison] = useState<StrategyComparison | null>(null);
  const [consolidationAnalysis, setConsolidationAnalysis] = useState<ConsolidationAnalysis | null>(null);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [payoffLoading, setPayoffLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });

  // Sorting state
  const [debtSortOrder, setDebtSortOrder] = useState<'balance' | 'rate' | 'payment'>('balance');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Priority Debts panel sorting
  const [prioritySortBy, setPrioritySortBy] = useState<'interest_rate' | 'balance'>('interest_rate');
  const [prioritySortDir, setPrioritySortDir] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [showAddDebtModal, setShowAddDebtModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [selectedDebtForPayment, setSelectedDebtForPayment] = useState<Debt | null>(null);

  // Payoff settings
  const [selectedStrategy, setSelectedStrategy] = useState<'avalanche' | 'snowball'>('avalanche');
  const [extraPayment, setExtraPayment] = useState<number>(0);

  const [debtForm, setDebtForm] = useState({
    name: '',
    type: '',
    balance: '',
    original_balance: '',
    interest_rate: '',
    minimum_payment: '',
    due_date: '',
    institution: '',
    account_number: '',
    notes: '',
    start_date: '',
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [consolidationForm, setConsolidationForm] = useState({
    new_interest_rate: '',
    loan_term_months: '60',
  });

  // File input ref for import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load data after categories are loaded
  useEffect(() => {
    if (categories.length > 0) {
      loadData();
    }
  }, [categories]);

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/debt-manager/categories');
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [debtsRes, summaryRes] = await Promise.all([
        apiClient.get('/debt-manager/debts'),
        apiClient.get('/debt-manager/summary'),
      ]);

      setDebts(debtsRes.data.debts);
      setSummary(summaryRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load debt data');
    } finally {
      setLoading(false);
    }
  };

  const loadPayoffPlan = async () => {
    if (debts.length === 0) return;

    setPayoffLoading(true);
    try {
      const [planRes, comparisonRes] = await Promise.all([
        apiClient.post('/debt-manager/payoff-plan', {
          strategy: selectedStrategy,
          extra_monthly_payment: extraPayment,
        }),
        apiClient.post('/debt-manager/compare-strategies', {
          extra_monthly_payment: extraPayment,
        }),
      ]);

      setPayoffPlan(planRes.data);
      setStrategyComparison(comparisonRes.data);
    } catch (err: any) {
      console.error('Failed to load payoff plan:', err);
    } finally {
      setPayoffLoading(false);
    }
  };

  const loadConsolidationAnalysis = async () => {
    if (!consolidationForm.new_interest_rate) return;

    try {
      const response = await apiClient.post('/debt-manager/consolidation-analysis', {
        new_interest_rate: parseFloat(consolidationForm.new_interest_rate),
        new_term_months: parseInt(consolidationForm.loan_term_months),
      });
      setConsolidationAnalysis(response.data);
    } catch (err: any) {
      console.error('Failed to analyze consolidation:', err);
    }
  };

  const loadAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const response = await apiClient.post('/debt-manager/analyze');
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Failed to load analysis:', err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Effect to reload payoff plan when strategy or extra payment changes
  useEffect(() => {
    if (activeTab === 'payoff' && debts.length > 0) {
      loadPayoffPlan();
    }
  }, [selectedStrategy, extraPayment, activeTab, debts.length]);

  const handleAddDebt = async () => {
    if (!debtForm.name || !debtForm.type || !debtForm.balance || !debtForm.interest_rate || !debtForm.minimum_payment) {
      return;
    }

    try {
      const payload = {
        name: debtForm.name,
        type: debtForm.type,
        balance: parseFloat(debtForm.balance),
        original_balance: debtForm.original_balance ? parseFloat(debtForm.original_balance) : parseFloat(debtForm.balance),
        interest_rate: parseFloat(debtForm.interest_rate),
        minimum_payment: parseFloat(debtForm.minimum_payment),
        due_date: debtForm.due_date ? parseInt(debtForm.due_date) : null,
        institution: debtForm.institution || null,
        account_number: debtForm.account_number || null,
        notes: debtForm.notes || null,
        start_date: debtForm.start_date || null,
      };

      if (editingDebt) {
        await apiClient.put(`/debt-manager/debts/${editingDebt.id}`, payload);
      } else {
        await apiClient.post('/debt-manager/debts', payload);
      }

      setShowAddDebtModal(false);
      setEditingDebt(null);
      resetDebtForm();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save debt');
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedDebtForPayment || !paymentForm.amount) return;

    try {
      await apiClient.post('/debt-manager/payments', {
        debt_id: selectedDebtForPayment.id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes || null,
      });

      setShowPaymentModal(false);
      setSelectedDebtForPayment(null);
      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeleteDebt = async (id: string) => {
    if (!window.confirm(t('debtManager:confirmDeleteDebt', 'Are you sure you want to delete this debt?'))) {
      return;
    }

    try {
      await apiClient.delete(`/debt-manager/debts/${id}`);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete debt');
    }
  };

  const handleEditDebt = (debt: Debt) => {
    setEditingDebt(debt);
    setDebtForm({
      name: debt.name,
      type: debt.type,
      balance: debt.balance.toString(),
      original_balance: debt.original_balance.toString(),
      interest_rate: debt.interest_rate.toString(),
      minimum_payment: debt.minimum_payment.toString(),
      due_date: debt.due_date?.toString() || '',
      institution: debt.institution || '',
      account_number: debt.account_number || '',
      notes: debt.notes || '',
      start_date: debt.start_date || '',
    });
    setShowAddDebtModal(true);
  };

  const resetDebtForm = () => {
    setDebtForm({
      name: '',
      type: '',
      balance: '',
      original_balance: '',
      interest_rate: '',
      minimum_payment: '',
      due_date: '',
      institution: '',
      account_number: '',
      notes: '',
      start_date: '',
    });
  };

  const handleClearAllData = async () => {
    if (!window.confirm(t('debtManager:confirmClearAll', 'Are you sure you want to clear ALL debt data? This cannot be undone.'))) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/debt-manager/clear-all');
      alert(response.data.message);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const handleImportQuicken = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('debtManager:importing', 'Importing Debt Data...'),
      subMessage: isPdf
        ? t('debtManager:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('debtManager:importingCsvHint', 'Analyzing and extracting debt information')
    });

    try {
      await apiClient.post('/debt-manager/import/quicken', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import file');
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportFromNetWorth = async () => {
    setLoadingOverlay({
      isVisible: true,
      message: t('debtManager:importingFromNetWorth', 'Importing from Net Worth Tracker...'),
      subMessage: t('debtManager:importingFromNetWorthHint', 'Checking for liabilities to import')
    });

    try {
      const response = await apiClient.post('/debt-manager/import/from-net-worth');
      if (response.data.imported_count > 0) {
        loadData();
      } else {
        // Show warning only when nothing was found to import
        setError(t('debtManager:noDebtsInNetWorth', 'No liabilities found in Net Worth Tracker to import'));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import from Net Worth Tracker');
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/debt-manager/export/csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_debts_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export CSV');
    }
  };

  const generateExportContent = () => {
    let content = `Debt Manager Summary\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;

    content += `=== SUMMARY ===\n`;
    content += `Total Debt: ${formatCurrency(summary?.total_debt || 0)}\n`;
    content += `Total Minimum Payments: ${formatCurrency(summary?.total_minimum_payments || 0)}\n`;
    content += `Average Interest Rate: ${(summary?.average_interest_rate || 0).toFixed(2)}%\n`;
    content += `Number of Debts: ${summary?.debt_count || 0}\n\n`;

    content += `=== DEBTS ===\n`;
    debts.forEach(debt => {
      content += `\n${debt.name}\n`;
      content += `  Type: ${getCategoryName(debt.type)}\n`;
      content += `  Balance: ${formatCurrency(debt.balance)}\n`;
      content += `  Interest Rate: ${debt.interest_rate}%\n`;
      content += `  Minimum Payment: ${formatCurrency(debt.minimum_payment)}\n`;
      if (debt.institution) content += `  Institution: ${debt.institution}\n`;
    });

    return content;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    try {
      const content = generateExportContent();
      const response = await apiClient.post('/export/document', {
        content,
        title: 'Debt Manager Summary',
        format,
      }, {
        responseType: 'blob',
      });

      const extension = format === 'word' ? 'docx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_debt_summary_${new Date().toISOString().slice(0, 10)}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export document');
    }
  };

  const handlePrint = () => {
    const content = generateExportContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Debt Manager Summary</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; white-space: pre-wrap; }
              h1 { color: #d97706; }
            </style>
          </head>
          <body>
            <h1>Debt Manager Summary</h1>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getCategoryName = (typeId: string): string => {
    const category = categories.find((c) => c.id === typeId);
    return category?.name || typeId;
  };

  const getDebtIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      credit_card: CreditCardIcon,
      student_loan: AcademicCapIcon,
      auto_loan: TruckIcon,
      mortgage: HomeIcon,
      personal_loan: BanknotesIcon,
      medical_debt: DocumentTextIcon,
      business_loan: BuildingOffice2Icon,
      home_equity: HomeIcon,
      payday_loan: ExclamationTriangleIcon,
      tax_debt: DocumentTextIcon,
    };
    const Icon = iconMap[type] || CreditCardIcon;
    return <Icon className="h-5 w-5" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (rate: number) => {
    return `${rate.toFixed(2)}%`;
  };

  // Sorted debts
  const sortedDebts = [...debts].sort((a, b) => {
    let comparison = 0;
    switch (debtSortOrder) {
      case 'balance':
        comparison = b.balance - a.balance;
        break;
      case 'rate':
        comparison = b.interest_rate - a.interest_rate;
        break;
      case 'payment':
        comparison = b.minimum_payment - a.minimum_payment;
        break;
    }
    return sortDirection === 'desc' ? comparison : -comparison;
  });

  // Prepare chart data
  const debtChartData = Object.entries(summary?.debts_by_type || {}).map(([type, value]) => ({
    name: getCategoryName(type),
    value: value,
  }));

  // Progress calculation
  const calculateProgress = (debt: Debt) => {
    if (!debt.original_balance || debt.original_balance === 0) return 0;
    const paid = debt.original_balance - debt.balance;
    return Math.round((paid / debt.original_balance) * 100);
  };

  // Custom tooltip for payoff chart
  const CustomPayoffTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">
          {t('debtManager:month', 'Month')} {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between text-sm gap-4">
            <span className="text-gray-600 dark:text-gray-400">{entry.name}</span>
            <span className="text-gray-900 dark:text-white font-medium">
              {formatCurrency(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCardIcon className="h-8 w-8 text-amber-500" />
            {t('debtManager:title', 'Debt Manager')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('debtManager:subtitle', 'Track debts, plan payoff strategies, and achieve financial freedom')}
          </p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Import/Export/Clear */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,.qif,.QIF,.csv,.CSV,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const ext = file.name.toLowerCase().split('.').pop();
                  if (!['qif', 'csv', 'pdf'].includes(ext || '')) {
                    alert('Please select a QIF, CSV, or PDF file.');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                }
                handleImportQuicken(e);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('debtManager:importQuicken', 'Import from Quicken or PDF (QIF/CSV/PDF)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              onExportCSV={handleExportCSV}
              showCSV={true}
              showPrint={true}
            />
            <button
              onClick={handleImportFromNetWorth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('debtManager:importFromNetWorth', 'Import from Net Worth Tracker')}
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
            <button
              onClick={handleClearAllData}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('debtManager:clearAllData', 'Clear All Data')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Add Debt Button */}
          <Button onClick={() => { resetDebtForm(); setShowAddDebtModal(true); }}>
            <PlusIcon className="h-5 w-5 mr-1" />
            {t('debtManager:addDebt', 'Add Debt')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {(['overview', 'debts', 'payoff', 'analysis'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'analysis' && !analysis) {
                loadAnalysis();
              }
              if (tab === 'payoff' && !payoffPlan && debts.length > 0) {
                loadPayoffPlan();
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab === 'overview' && t('debtManager:tabs.overview', 'Overview')}
            {tab === 'debts' && t('debtManager:tabs.debts', 'My Debts')}
            {tab === 'payoff' && (
              <span className="flex items-center gap-1">
                <CalculatorIcon className="h-4 w-4" />
                {t('debtManager:tabs.payoff', 'Payoff Planner')}
              </span>
            )}
            {tab === 'analysis' && (
              <span className="flex items-center gap-1">
                <SparklesIcon className="h-4 w-4" />
                {t('debtManager:tabs.analysis', 'AI Analysis')}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('debtManager:totalDebt', 'Total Debt')}
                    </span>
                    <CreditCardIcon className="h-5 w-5 text-red-500" />
                  </div>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {formatCurrency(summary?.total_debt || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {summary?.debt_count || 0} {t('debtManager:accounts', 'accounts')}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('debtManager:monthlyPayments', 'Monthly Payments')}
                    </span>
                    <BanknotesIcon className="h-5 w-5 text-amber-500" />
                  </div>
                  <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                    {formatCurrency(summary?.total_minimum_payments || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('debtManager:minimumDue', 'minimum due')}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('debtManager:avgInterestRate', 'Avg Interest Rate')}
                    </span>
                    <ArrowTrendingDownIcon className="h-5 w-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {formatPercent(summary?.average_interest_rate || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('debtManager:weightedAverage', 'weighted average')}
                  </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {t('debtManager:monthlyInterest', 'Monthly Interest')}
                    </span>
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                  </div>
                  <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency(summary?.total_monthly_interest || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('debtManager:accruing', 'accruing monthly')}
                  </p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Debt Breakdown Pie Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('debtManager:debtBreakdown', 'Debt Breakdown by Type')}
                  </h3>
                  {debtChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={debtChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {debtChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={DEBT_COLORS[index % DEBT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      {t('debtManager:noDebts', 'No debts recorded yet')}
                    </div>
                  )}
                </div>

                {/* Top Debts List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('debtManager:priorityDebts', 'Priority Debts')}
                    </h3>
                    {debts.length > 0 && (
                      <div className="flex items-center gap-2">
                        <select
                          value={prioritySortBy}
                          onChange={(e) => setPrioritySortBy(e.target.value as 'interest_rate' | 'balance')}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="interest_rate">{t('debtManager:sortByRate', 'Interest Rate')}</option>
                          <option value="balance">{t('debtManager:sortByBalance', 'Balance')}</option>
                        </select>
                        <select
                          value={prioritySortDir}
                          onChange={(e) => setPrioritySortDir(e.target.value as 'asc' | 'desc')}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="desc">{t('debtManager:highToLow', 'High to Low')}</option>
                          <option value="asc">{t('debtManager:lowToHigh', 'Low to High')}</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {debts.length > 0 ? (
                    <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                      {[...debts]
                        .sort((a, b) => {
                          const aVal = prioritySortBy === 'interest_rate' ? a.interest_rate : a.balance;
                          const bVal = prioritySortBy === 'interest_rate' ? b.interest_rate : b.balance;
                          return prioritySortDir === 'desc' ? bVal - aVal : aVal - bVal;
                        })
                        .map((debt, index) => (
                          <div key={debt.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : index === 2 ? 'bg-yellow-500' : 'bg-gray-400'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{debt.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatPercent(debt.interest_rate)} APR
                                </p>
                              </div>
                            </div>
                            <span className="font-semibold text-red-600 dark:text-red-400">
                              {formatCurrency(debt.balance)}
                            </span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      {t('debtManager:noDebts', 'No debts recorded yet')}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                  {t('debtManager:quickActions', 'Quick Actions')}
                </h3>
                <div className="flex flex-wrap gap-4 mt-3">
                  <button
                    onClick={() => { resetDebtForm(); setShowAddDebtModal(true); }}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t('debtManager:addNewDebt', 'Add New Debt')}
                  </button>
                  <button
                    onClick={() => setActiveTab('payoff')}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    <CalculatorIcon className="h-4 w-4" />
                    {t('debtManager:planPayoff', 'Plan Payoff Strategy')}
                  </button>
                  <button
                    onClick={() => { setActiveTab('analysis'); loadAnalysis(); }}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {t('debtManager:getAIAdvice', 'Get AI Advice')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Debts Tab */}
          {activeTab === 'debts' && (
            <div className="space-y-4">
              {/* Header with Sort */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('debtManager:yourDebts', 'Your Debts')}
                  </h2>
                  {debts.length > 1 && (
                    <div className="flex items-center gap-2">
                      <select
                        value={debtSortOrder}
                        onChange={(e) => setDebtSortOrder(e.target.value as any)}
                        className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="balance">{t('debtManager:sortByBalance', 'Sort by Balance')}</option>
                        <option value="rate">{t('debtManager:sortByRate', 'Sort by Rate')}</option>
                        <option value="payment">{t('debtManager:sortByPayment', 'Sort by Payment')}</option>
                      </select>
                      <button
                        onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600"
                      >
                        {sortDirection === 'desc' ? (
                          <ArrowDownIcon className="h-4 w-4" />
                        ) : (
                          <ArrowUpIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <Button onClick={() => { resetDebtForm(); setShowAddDebtModal(true); }}>
                  <PlusIcon className="h-5 w-5 mr-1" />
                  {t('debtManager:addDebt', 'Add Debt')}
                </Button>
              </div>

              {/* Debts List */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {debts.length > 0 ? (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedDebts.map((debt) => (
                      <div
                        key={debt.id}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                              {getDebtIcon(debt.type)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {debt.name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {getCategoryName(debt.type)}
                                {debt.institution && ` • ${debt.institution}`}
                                {` • ${formatPercent(debt.interest_rate)} APR`}
                              </p>
                              {/* Progress bar */}
                              <div className="mt-2 w-48">
                                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                  <span>{calculateProgress(debt)}% paid off</span>
                                  <span>{formatCurrency(debt.original_balance - debt.balance)} / {formatCurrency(debt.original_balance)}</span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full transition-all duration-300"
                                    style={{ width: `${calculateProgress(debt)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                                {formatCurrency(debt.balance)}
                              </span>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {formatCurrency(debt.minimum_payment)}/mo min
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setSelectedDebtForPayment(debt);
                                  setPaymentForm({
                                    ...paymentForm,
                                    amount: debt.minimum_payment.toString(),
                                  });
                                  setShowPaymentModal(true);
                                }}
                                className="p-2 text-gray-500 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                title={t('debtManager:recordPayment', 'Record Payment')}
                              >
                                <BanknotesIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditDebt(debt)}
                                className="p-2 text-gray-500 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteDebt(debt.id)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                    <CreditCardIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('debtManager:noDebtsYet', 'No debts recorded yet')}</p>
                    <Button onClick={() => setShowAddDebtModal(true)} className="mt-4">
                      <PlusIcon className="h-5 w-5 mr-1" />
                      {t('debtManager:addFirstDebt', 'Add your first debt')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payoff Planner Tab */}
          {activeTab === 'payoff' && (
            <div className="space-y-6">
              {debts.length === 0 ? (
                <div className="text-center py-12">
                  <CalculatorIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('debtManager:addDebtsFirst', 'Add some debts first to create a payoff plan')}
                  </p>
                  <Button onClick={() => setShowAddDebtModal(true)}>
                    <PlusIcon className="h-5 w-5 mr-1" />
                    {t('debtManager:addDebt', 'Add Debt')}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Strategy Selection */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      {t('debtManager:chooseStrategy', 'Choose Your Strategy')}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Avalanche Strategy */}
                      <button
                        onClick={() => setSelectedStrategy('avalanche')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedStrategy === 'avalanche'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                            <ArrowTrendingDownIcon className="h-5 w-5" />
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {t('debtManager:avalanche', 'Avalanche Method')}
                          </span>
                          {selectedStrategy === 'avalanche' && (
                            <CheckCircleIcon className="h-5 w-5 text-blue-500 ml-auto" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('debtManager:avalancheDesc', 'Pay highest interest rate first. Saves the most money on interest.')}
                        </p>
                      </button>

                      {/* Snowball Strategy */}
                      <button
                        onClick={() => setSelectedStrategy('snowball')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedStrategy === 'snowball'
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                            <CheckCircleIcon className="h-5 w-5" />
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {t('debtManager:snowball', 'Snowball Method')}
                          </span>
                          {selectedStrategy === 'snowball' && (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 ml-auto" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('debtManager:snowballDesc', 'Pay smallest balance first. Builds momentum with quick wins.')}
                        </p>
                      </button>
                    </div>

                    {/* Extra Payment Input */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('debtManager:extraMonthlyPayment', 'Extra Monthly Payment')}:
                        </label>
                        <div className="relative w-40">
                          <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                          <input
                            type="number"
                            min="0"
                            step="50"
                            value={extraPayment}
                            onChange={(e) => setExtraPayment(parseFloat(e.target.value) || 0)}
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                        {t('debtManager:extraPaymentExplanation', 'This is the additional amount you can afford to pay each month beyond your minimum payments. It will be applied to the highest-priority debt (based on your chosen strategy) to accelerate your payoff.')}
                      </p>
                    </div>

                    {/* How It Works */}
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                        {t('debtManager:howItWorks', 'How It Works')}
                      </h4>
                      <ol className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                        <li>{t('debtManager:howItWorks1', 'Each month, make the minimum payment on ALL your debts')}</li>
                        <li>{t('debtManager:howItWorks2', 'Apply your extra payment to the #1 priority debt (highest rate or smallest balance)')}</li>
                        <li>{t('debtManager:howItWorks3', 'When a debt is paid off, roll its payment into the next priority debt')}</li>
                        <li>{t('debtManager:howItWorks4', 'Repeat until all debts are paid off')}</li>
                      </ol>
                    </div>
                  </div>

                  {/* Strategy Comparison */}
                  {strategyComparison && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {t('debtManager:strategyComparison', 'Strategy Comparison')}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('debtManager:avalanche', 'Avalanche')}</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {strategyComparison.avalanche.total_months} {t('debtManager:months', 'months')}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatCurrency(strategyComparison.avalanche.total_interest)} {t('debtManager:inInterest', 'in interest')}
                          </p>
                        </div>

                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('debtManager:snowball', 'Snowball')}</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {strategyComparison.snowball.total_months} {t('debtManager:months', 'months')}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatCurrency(strategyComparison.snowball.total_interest)} {t('debtManager:inInterest', 'in interest')}
                          </p>
                        </div>

                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('debtManager:youSave', 'You Save (Avalanche)')}</p>
                          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                            {formatCurrency(strategyComparison.interest_savings)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {Math.abs(strategyComparison.time_difference)} {t('debtManager:monthsDifference', 'months difference')}
                          </p>
                        </div>
                      </div>

                      <p className="text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <SparklesIcon className="h-4 w-4 inline mr-1 text-amber-500" />
                        {strategyComparison.recommendation}
                      </p>
                    </div>
                  )}

                  {/* Payoff Order */}
                  {payoffPlan && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {t('debtManager:payoffOrder', 'Payoff Order')} ({selectedStrategy === 'avalanche' ? t('debtManager:avalanche', 'Avalanche') : t('debtManager:snowball', 'Snowball')})
                      </h3>

                      <div className="space-y-3">
                        {payoffPlan.payoff_order.map((item, index) => (
                          <div key={item.debt_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                index === 0 ? 'bg-amber-500' : 'bg-gray-400'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{item.debt_name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatCurrency(item.balance)} @ {formatPercent(item.interest_rate)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {t('debtManager:paidOffMonth', 'Paid off month')} {item.payoff_month}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {formatCurrency(item.total_interest_paid)} {t('debtManager:inInterest', 'in interest')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-amber-800 dark:text-amber-200">
                              {t('debtManager:debtFreeIn', 'Debt Free In')}
                            </p>
                            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                              {payoffPlan.total_months} {t('debtManager:months', 'months')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-amber-800 dark:text-amber-200">
                              {t('debtManager:totalInterest', 'Total Interest')}
                            </p>
                            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                              {formatCurrency(payoffPlan.total_interest)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Consolidation Option */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      {t('debtManager:consolidationAnalysis', 'Debt Consolidation Analysis')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {t('debtManager:consolidationDesc', 'See if consolidating your debts into a single loan could save you money.')}
                    </p>

                    <div className="flex items-end gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('debtManager:newInterestRate', 'New Interest Rate')}
                        </label>
                        <div className="relative w-32">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="50"
                            value={consolidationForm.new_interest_rate}
                            onChange={(e) => setConsolidationForm({ ...consolidationForm, new_interest_rate: e.target.value })}
                            className="w-full pr-7 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="7.5"
                          />
                          <span className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('debtManager:loanTerm', 'Loan Term')}
                        </label>
                        <select
                          value={consolidationForm.loan_term_months}
                          onChange={(e) => setConsolidationForm({ ...consolidationForm, loan_term_months: e.target.value })}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="36">36 {t('debtManager:months', 'months')}</option>
                          <option value="48">48 {t('debtManager:months', 'months')}</option>
                          <option value="60">60 {t('debtManager:months', 'months')}</option>
                          <option value="72">72 {t('debtManager:months', 'months')}</option>
                          <option value="84">84 {t('debtManager:months', 'months')}</option>
                        </select>
                      </div>
                      <Button onClick={loadConsolidationAnalysis} disabled={!consolidationForm.new_interest_rate}>
                        <CalculatorIcon className="h-5 w-5 mr-1" />
                        {t('debtManager:analyze', 'Analyze')}
                      </Button>
                    </div>

                    {consolidationAnalysis && (
                      <div className="mt-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                              {t('debtManager:currentSituation', 'Current Situation')}
                            </p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(consolidationAnalysis.current_situation.total_monthly_payment)}/mo
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatPercent(consolidationAnalysis.current_situation.weighted_avg_rate)} avg rate • {consolidationAnalysis.current_situation.months_to_payoff} months
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(consolidationAnalysis.current_situation.total_interest)} total interest
                            </p>
                          </div>

                          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                              {t('debtManager:withConsolidation', 'With Consolidation')}
                            </p>
                            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(consolidationAnalysis.consolidation_option.new_monthly_payment)}/mo
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatPercent(consolidationAnalysis.consolidation_option.new_rate)} rate • {consolidationAnalysis.consolidation_option.months_to_payoff} months
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(consolidationAnalysis.consolidation_option.total_interest)} total interest
                            </p>
                          </div>
                        </div>

                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <p className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                            {t('debtManager:potentialSavings', 'Potential Savings')}
                          </p>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:monthly', 'Monthly')}</p>
                              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                {formatCurrency(consolidationAnalysis.savings.monthly_savings)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:totalInterest', 'Total Interest')}</p>
                              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                {formatCurrency(consolidationAnalysis.savings.interest_savings)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:timeSaved', 'Time Saved')}</p>
                              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                {consolidationAnalysis.savings.time_savings} {t('debtManager:months', 'months')}
                              </p>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <SparklesIcon className="h-4 w-4 inline mr-1 text-amber-500" />
                          {consolidationAnalysis.recommendation}
                        </p>

                        {consolidationAnalysis.considerations.length > 0 && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <p className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                              {t('debtManager:considerations', 'Things to Consider')}
                            </p>
                            <ul className="space-y-1">
                              {consolidationAnalysis.considerations.map((item, index) => (
                                <li key={index} className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
                                  <span>•</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* AI Analysis Tab */}
          {activeTab === 'analysis' && (
            <div className="space-y-6">
              {analysisLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('debtManager:analyzingDebts', 'Analyzing your debt situation...')}
                    </p>
                  </div>
                </div>
              ) : analysis ? (
                <>
                  {/* Debt Health Score */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <SparklesIcon className="h-5 w-5 text-amber-500" />
                      {t('debtManager:debtHealthScore', 'Debt Health Score')}
                    </h3>
                    <div className="flex items-center gap-6">
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white ${
                        analysis.debt_health_score >= 80 ? 'bg-green-500' :
                        analysis.debt_health_score >= 60 ? 'bg-yellow-500' :
                        analysis.debt_health_score >= 40 ? 'bg-orange-500' : 'bg-red-500'
                      }`}>
                        {analysis.debt_health_score}
                      </div>
                      <div className="flex-1">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:totalDebt', 'Total Debt')}</span>
                            <p className="text-xl font-bold text-red-600 dark:text-red-400">
                              {formatCurrency(analysis.summary.total_debt)}
                            </p>
                          </div>
                          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:avgRate', 'Avg Rate')}</span>
                            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                              {formatPercent(analysis.summary.average_rate)}
                            </p>
                          </div>
                          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:monthlyPayments', 'Monthly Payments')}</span>
                            <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                              {formatCurrency(analysis.summary.monthly_payments)}
                            </p>
                          </div>
                          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t('debtManager:debtToIncome', 'Debt-to-Income')}</span>
                            <p className="text-xl font-bold text-gray-900 dark:text-white">
                              {analysis.summary.debt_to_income_estimate}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Strategy Recommendation */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
                    <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-3">
                      {t('debtManager:recommendedStrategy', 'Recommended Strategy')}
                    </h3>
                    <p className="text-amber-700 dark:text-amber-300">{analysis.strategy_recommendation}</p>

                    {/* Strategy Explanations */}
                    <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-700">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                        {t('debtManager:strategyExplanations', 'Strategy Explanations')}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-start gap-2 p-2 bg-amber-100/50 dark:bg-amber-800/20 rounded-lg">
                          <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                              {t('debtManager:avalanche', 'Avalanche Method')}
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              {t('debtManager:avalancheDesc', 'Pay highest interest rate first. Saves the most money on interest.')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-amber-100/50 dark:bg-amber-800/20 rounded-lg">
                          <InformationCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                              {t('debtManager:snowball', 'Snowball Method')}
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              {t('debtManager:snowballDesc', 'Pay smallest balance first. Builds momentum with quick wins.')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Priority Debts */}
                  {analysis.priority_debts && analysis.priority_debts.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4">
                        {t('debtManager:priorityDebts', 'Priority Debts to Address')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.priority_debts.map((debt, index) => (
                          <li key={index} className="flex items-start gap-2 text-red-700 dark:text-red-300">
                            <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {debt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Quick Wins */}
                  {analysis.quick_wins && analysis.quick_wins.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
                        {t('debtManager:quickWins', 'Quick Wins')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.quick_wins.map((win, index) => (
                          <li key={index} className="flex items-start gap-2 text-green-700 dark:text-green-300">
                            <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {win}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Plan */}
                  {analysis.action_plan && analysis.action_plan.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
                        {t('debtManager:actionPlan', 'Action Plan')}
                      </h3>
                      <ol className="space-y-2">
                        {analysis.action_plan.map((action, index) => (
                          <li key={index} className="flex items-start gap-3 text-blue-700 dark:text-blue-300">
                            <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </span>
                            {action}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Warnings */}
                  {analysis.warnings && analysis.warnings.length > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-6 border border-orange-200 dark:border-orange-800">
                      <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-4">
                        {t('debtManager:warnings', 'Important Warnings')}
                      </h3>
                      <ul className="space-y-2">
                        {analysis.warnings.map((warning, index) => (
                          <li key={index} className="flex items-start gap-2 text-orange-700 dark:text-orange-300">
                            <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            {warning}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Encouragement */}
                  {analysis.encouragement && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                      <p className="text-purple-700 dark:text-purple-300 text-lg">
                        {analysis.encouragement}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={loadAnalysis}
                    className="text-amber-600 dark:text-amber-400 text-sm font-medium hover:underline"
                  >
                    {t('debtManager:refreshAnalysis', 'Refresh AI Analysis')}
                  </button>
                </>
              ) : (
                <div className="text-center py-12">
                  <SparklesIcon className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {t('debtManager:noAnalysisYet', 'Click below to get AI-powered analysis of your debt situation')}
                  </p>
                  <Button onClick={loadAnalysis}>
                    <SparklesIcon className="h-5 w-5 mr-1" />
                    {t('debtManager:getAnalysis', 'Get AI Analysis')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Debt Modal */}
      {showAddDebtModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingDebt
                  ? t('debtManager:editDebt', 'Edit Debt')
                  : t('debtManager:addDebt', 'Add Debt')}
              </h2>
              <button
                onClick={() => { setShowAddDebtModal(false); setEditingDebt(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:debtName', 'Debt Name')} *
                </label>
                <input
                  type="text"
                  value={debtForm.name}
                  onChange={(e) => setDebtForm({ ...debtForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('debtManager:debtNamePlaceholder', 'e.g., Chase Credit Card')}
                  required
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:debtType', 'Debt Type')} *
                </label>
                <select
                  value={debtForm.type}
                  onChange={(e) => setDebtForm({ ...debtForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">{t('debtManager:selectType', 'Select a type')}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Current Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:currentBalance', 'Current Balance')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={debtForm.balance}
                    onChange={(e) => setDebtForm({ ...debtForm, balance: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Original Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:originalBalance', 'Original Balance')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={debtForm.original_balance}
                    onChange={(e) => setDebtForm({ ...debtForm, original_balance: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('debtManager:sameAsCurrent', 'Same as current if blank')}
                  />
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:interestRate', 'Interest Rate (APR)')} *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={debtForm.interest_rate}
                    onChange={(e) => setDebtForm({ ...debtForm, interest_rate: e.target.value })}
                    className="w-full pr-7 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                  <span className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">%</span>
                </div>
              </div>

              {/* Minimum Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:minimumPayment', 'Minimum Monthly Payment')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={debtForm.minimum_payment}
                    onChange={(e) => setDebtForm({ ...debtForm, minimum_payment: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:dueDate', 'Due Date (day of month)')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={debtForm.due_date}
                  onChange={(e) => setDebtForm({ ...debtForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="15"
                />
              </div>

              {/* Institution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:institution', 'Institution/Lender')}
                </label>
                <input
                  type="text"
                  value={debtForm.institution}
                  onChange={(e) => setDebtForm({ ...debtForm, institution: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('debtManager:institutionPlaceholder', 'e.g., Chase Bank')}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:notes', 'Notes')}
                </label>
                <textarea
                  value={debtForm.notes}
                  onChange={(e) => setDebtForm({ ...debtForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowAddDebtModal(false); setEditingDebt(null); }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleAddDebt}
                fullWidth
                disabled={!debtForm.name || !debtForm.type || !debtForm.balance || !debtForm.interest_rate || !debtForm.minimum_payment}
              >
                {editingDebt
                  ? t('common:buttons.save', 'Save')
                  : t('debtManager:addDebt', 'Add Debt')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedDebtForPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('debtManager:recordPayment', 'Record Payment')}
              </h2>
              <button
                onClick={() => { setShowPaymentModal(false); setSelectedDebtForPayment(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white">{selectedDebtForPayment.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('debtManager:currentBalance', 'Current Balance')}: {formatCurrency(selectedDebtForPayment.balance)}
              </p>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:paymentAmount', 'Payment Amount')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:paymentDate', 'Payment Date')}
                </label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  style={dateInputStyle}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('debtManager:notes', 'Notes')}
                </label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('debtManager:paymentNotesPlaceholder', 'e.g., Extra payment')}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowPaymentModal(false); setSelectedDebtForPayment(null); }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleRecordPayment}
                fullWidth
                disabled={!paymentForm.amount}
              >
                {t('debtManager:recordPayment', 'Record Payment')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtManager;
